const TRASH_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/></svg>';

// Free gift lines (hidden $0 "Free Gift" products) carry this property with the
// product id of the item they belong to; their quantity follows that item.
const GIFT_FOR_PROPERTY = '_free_gift_for';
const GIFT_VALUE_PROPERTY = '_gift_value';

function isGiftItem(item) {
  return Boolean(item?.properties && item.properties[GIFT_FOR_PROPERTY]);
}

// Number of real items, so a free gift does not count as an extra item.
function paidItemCount(cart) {
  return (cart?.items || []).reduce((sum, item) => sum + (isGiftItem(item) ? 0 : item.quantity || 0), 0);
}

// Returns the /cart/update.js payload that keeps every gift line equal to the
// quantity of the product it belongs to (0 removes it), or null when in sync.
function giftUpdates(cart) {
  const productQuantities = new Map();
  (cart?.items || []).forEach((item) => {
    if (isGiftItem(item)) return;
    const id = String(item.product_id);
    productQuantities.set(id, (productQuantities.get(id) || 0) + (item.quantity || 0));
  });

  const updates = {};
  let changed = false;
  (cart?.items || []).forEach((item) => {
    // A "Free Gift" product added on its own (product page, link) is not free on its own: remove it.
    if (!isGiftItem(item) && item.product_type === 'Free Gift' && item.quantity > 0) {
      updates[item.key] = 0;
      changed = true;
      return;
    }
    if (!isGiftItem(item)) return;
    const desired = productQuantities.get(String(item.properties[GIFT_FOR_PROPERTY])) || 0;
    if (desired !== item.quantity) {
      updates[item.key] = desired;
      changed = true;
    }
  });

  return changed ? updates : null;
}

async function syncGiftLines(cart) {
  const updates = giftUpdates(cart);
  if (!updates) return cart;

  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ updates })
  });
  if (!response.ok) return cart;
  return response.json();
}

window.IselleGiftSync = { syncGiftLines, giftUpdates, isGiftItem };

class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.showSavings = this.drawer.dataset.cartDrawerShowSavings === 'true';
    this.saveLabel = this.drawer.dataset.cartDrawerSaveLabel || 'Save';
    this.emptyText = this.drawer.dataset.cartDrawerEmptyText || 'Your cart is empty';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';

    this.bindUI();
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      await response.json();
      await this.addFreeGift(form, formData);
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
    } finally {
      this.setAddButtonState(false);
    }
  }

  async addFreeGift(form, formData) {
    const marker = form.querySelector('[data-free-gift-variant]');
    const variantId = Number(marker?.dataset.freeGiftVariant || 0);
    if (!variantId) return;

    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: Math.max(1, Number(formData.get('quantity')) || 1),
              properties: {
                [GIFT_FOR_PROPERTY]: marker.dataset.freeGiftFor,
                [GIFT_VALUE_PROPERTY]: '4995'
              }
            }
          ]
        })
      });
    } catch (error) {
      console.error(error);
    }
    // Quantities are corrected by syncGiftLines on the next refresh.
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }

    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
    }
  }

  async handleDrawerChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Math.max(0, Number(input.value) || 0);
    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update cart item');
      }

      const cart = await syncGiftLines(await response.json());
      this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;

    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (currentCount > 0) {
      this.showSkeleton(Math.min(currentCount, 3));
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });
      const cart = await syncGiftLines(await response.json());
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    const itemCount = paidItemCount(cart);

    if (this.drawerCount) {
      this.drawerCount.textContent = itemCount;
    }

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    const compareTotal = (cart.items || []).reduce(
      (sum, item) => sum + (isGiftItem(item) ? this.giftValue(item) : item.compare_at_line_price || item.final_line_price || 0),
      0
    );
    if (this.compareSubtotalNode) {
      const showCompareSubtotal = this.showCompareAt && compareTotal > (cart.total_price || 0);
      this.compareSubtotalNode.hidden = !showCompareSubtotal;
      this.compareSubtotalNode.textContent = showCompareSubtotal ? this.formatMoney(compareTotal) : '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }

    this.drawer.classList.toggle('is-empty', itemCount === 0);

    const discountsNode = this.drawer.querySelector('[data-cart-drawer-discounts]');
    if (discountsNode) {
      // Sum the discount per title from the line items: present in every cart
      // response (cart.js, add.js, change.js), unlike the cart-level list.
      const totals = new Map();
      (cart.items || []).forEach((item) => {
        (item.discounts || []).forEach((discount) => {
          if (!discount.title) return;
          totals.set(discount.title, (totals.get(discount.title) || 0) + (discount.amount || 0));
        });
      });
      const discounts = [...totals].map(([title, amount]) => ({ title, total_allocated_amount: amount }));
      discountsNode.hidden = discounts.length === 0;
      discountsNode.innerHTML = discounts
        .map(
          (discount) =>
            `<p><span>Discounts</span> <span class="cart-drawer__discount-value">${this.escapeHtml(discount.title)} (-${this.formatMoney(discount.total_allocated_amount)})</span></p>`
        )
        .join('');
    }

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-text">${this.escapeHtml(this.emptyText)}</p>
        </div>
      `;
      return;
    }

    // Show each free gift directly below the item it belongs to.
    const gifts = cart.items.filter(isGiftItem);
    const orderedItems = [];
    cart.items.filter((item) => !isGiftItem(item)).forEach((item) => {
      orderedItems.push(item);
      gifts
        .filter((gift) => !orderedItems.includes(gift) && String(gift.properties[GIFT_FOR_PROPERTY]) === String(item.product_id))
        .forEach((gift) => orderedItems.push(gift));
    });
    gifts.filter((gift) => !orderedItems.includes(gift)).forEach((gift) => orderedItems.push(gift));

    const itemsMarkup = orderedItems
      .map((item) => {
        const imageUrl = item.image
          ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=320&height=400&crop=center`
          : '';
        const imageMarkup = item.image
          ? `<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">
              <img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" width="320" height="400" loading="lazy">
            </a>`
          : `<div class="cart-drawer__item-media"></div>`;

        const options = (item.options_with_values || []).filter((option) => option.value !== 'Default Title');
        const variantMarkup = options.length
          ? `<p class="cart-drawer__item-variant">${options
              .map((option) => `${this.escapeHtml(option.name)}: ${this.escapeHtml(option.value)}`)
              .join(', ')}</p>`
          : '';
        const propertyMarkup = Object.entries(item.properties || {})
          .filter(([key, value]) => value && !String(key).startsWith('_'))
          .map(([key, value]) => `<p class="cart-drawer__item-property">${this.escapeHtml(key)}: ${this.escapeHtml(value)}</p>`)
          .join('');

        const compareLine = Math.max(item.compare_at_line_price || 0, item.original_line_price || 0);
        const lineSavings = compareLine - (item.final_line_price || 0);
        const comparePriceMarkup =
          this.showCompareAt && lineSavings > 0
            ? `<s class="cart-drawer__item-compare-price">${this.formatMoney(compareLine)}</s>`
            : '';
        const savingsMarkup =
          this.showSavings && lineSavings > 0
            ? `<span class="cart-drawer__item-savings">(${this.escapeHtml(this.saveLabel)} ${this.formatMoney(lineSavings)})</span>`
            : '';

        if (isGiftItem(item)) {
          return this.giftItemMarkup(item, imageMarkup);
        }

        const isPending = this.pendingItemKeys.has(item.key);
        const disabled = isPending ? 'disabled' : '';

        return `
          <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${item.key}" aria-busy="${isPending ? 'true' : 'false'}">
            ${imageMarkup}
            <div class="cart-drawer__item-content">
              <div class="cart-drawer__item-top">
                <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
                <button type="button" class="cart-drawer__remove" data-cart-remove ${disabled} aria-label="${this.escapeHtml(this.removeLabel)}: ${this.escapeHtml(item.product_title)}">${TRASH_ICON}</button>
              </div>
              ${variantMarkup}${propertyMarkup}
              <p class="cart-drawer__item-prices">
                ${comparePriceMarkup}
                <span class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</span>
                ${savingsMarkup}
              </p>
              <div class="cart-drawer__quantity">
                <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${disabled}>-</button>
                <input type="number" class="cart-drawer__quantity-value" value="${item.quantity}" min="1" aria-label="${this.escapeHtml(this.quantityLabel)}" data-cart-quantity-input ${disabled}>
                <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${disabled}>+</button>
              </div>
            </div>
          </li>
        `;
      })
      .join('');

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${itemsMarkup}</ul>`;
  }

  giftValue(item) {
    return (Number(item.properties?.[GIFT_VALUE_PROPERTY]) || 0) * (item.quantity || 0);
  }

  giftItemMarkup(item, imageMarkup) {
    const value = this.giftValue(item);
    const quantity = item.quantity > 1 ? `<p class="cart-drawer__item-variant">Qty: ${item.quantity}</p>` : '';

    return `
      <li class="cart-drawer__item cart-drawer__item--gift" data-cart-gift-key="${item.key}">
        ${imageMarkup.replace(/<a [^>]*class="cart-drawer__item-media">/, '<div class="cart-drawer__item-media">').replace(/<\/a>\s*$/, '</div>')}
        <div class="cart-drawer__item-content">
          <div class="cart-drawer__item-top">
            <span class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</span>
          </div>
          <p class="cart-drawer__item-gift-badge">Free gift · Today only</p>
          ${quantity}
          <p class="cart-drawer__item-prices">
            ${value > 0 ? `<s class="cart-drawer__item-compare-price">${this.formatMoney(value)}</s>` : ''}
            <span class="cart-drawer__item-price cart-drawer__item-price--free">FREE</span>
          </p>
        </div>
      </li>
    `;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!(this.showCompareAt || this.showSavings)) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!(this.showCompareAt || this.showSavings) || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const controller = new CartDrawerController();

  // Gift quantities can drift when the cart is changed outside the drawer
  // (cart page, apps); fix them once per page load.
  if (document.querySelector('[data-cart-has-gift]')) {
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then(async (cart) => {
        if (!giftUpdates(cart)) return;
        await syncGiftLines(cart);
        if (document.querySelector('.cart-page')) {
          window.location.reload();
        } else if (controller.drawer) {
          controller.refresh();
        }
      })
      .catch((error) => console.error(error));
  }
});
